import { type ModalProps } from '~/util';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '~/primitives/sheet';
import { useAppForm } from '~/util/hooks/useAppForm';
import { formOptions } from '@tanstack/react-form';
import { useList } from '~/util/hooks/useList';
import { useApiMutation } from '~/util/hooks/useApiMutation';

import { useEffect, useState } from 'react';
import { packTierFormInput, packTierFormSchema } from '~/util/forms/packtier';
import {
    deletePacktierMutation,
    updatePacktierMutation,
} from '~/util/mutations/staff/packs';
import { PackTier } from '~/util/types/packs';
import { NewPackSheet } from './-NewPackSheet';
import { Button } from '~/primitives/button';
import { Plus, Trash } from 'lucide-react';
import ConfirmModal from '~/components/ConfirmModal';

export const EditPackTierSheet: React.FC<
    {
        packtier: PackTier;
    } & ModalProps
> = ({ packtier, ...modalProps }) => {
    const list = useList();

    const [newPackSheetOpen, setNewPackSheetOpen] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);

    const mutation = useApiMutation({
        ...updatePacktierMutation(),
        onSuccess: () => modalProps.onOpenChange?.(false),
    });

    const deleteMutation = useApiMutation({
        ...deletePacktierMutation(),
        onSuccess: () => modalProps.onOpenChange?.(false),
    });

    const form = useAppForm(
        formOptions({
            formId: 'edit-packtier-staff',
            defaultValues: {
                list,
                name: packtier.name,
                color: packtier.color,
                placement: packtier.placement,
            } as packTierFormInput,
            validators: {
                onChangeAsyncDebounceMs: 500,
                onChange: packTierFormSchema,
            },
            onSubmit: async ({ value }) => {
                mutation.mutate({ data: value, id: packtier.id });
            },
        }),
    );

    useEffect(() => {
        form.reset({
            list,
        });
    }, [list, packtier, form]);

    return (
        <>
            <Sheet {...modalProps}>
                <SheetContent className="w-full sm:w-auto sm:min-w-150 sm:max-w-150 overflow-y-scroll">
                    <SheetHeader>
                        <SheetTitle>Edit pack tier</SheetTitle>
                        <SheetDescription>
                            Update pack tier information, create a new pack, or
                            delete this tier.
                        </SheetDescription>
                    </SheetHeader>
                    <form
                        id="edit-packtier-staff"
                        onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            form.handleSubmit();
                        }}
                    >
                        <div className="flex flex-col justify-start items-start gap-x-6 gap-y-3 p-6 pt-6">
                            <form.AppField name="name">
                                {(field) => (
                                    <field.TextField
                                        label="Name"
                                        required
                                        placeholder="Name of the tier"
                                    />
                                )}
                            </form.AppField>
                            <form.AppField name="color">
                                {(field) => (
                                    <field.ColorField
                                        label="Color"
                                        required
                                        placeholder="Color of the tier"
                                    />
                                )}
                            </form.AppField>
                            <form.AppField name="placement">
                                {(field) => (
                                    <field.NumberField
                                        label="Placement order"
                                        required
                                        placeholder="Position of the tier among other tiers"
                                    />
                                )}
                            </form.AppField>
                        </div>

                        <SheetFooter className="justify-center">
                            <div className="flex flex-col w-full gap-2">
                                <form.AppForm>
                                    <form.SubscribeButton label="Edit pack tier" />
                                </form.AppForm>
                                <div className="flex w-full gap-1">
                                    <Button
                                        type="button"
                                        className="bg-confirm hover:bg-confirm w-2/3"
                                        onClick={() =>
                                            setNewPackSheetOpen(true)
                                        }
                                    >
                                        <Plus className="mr-2" />
                                        Add Pack
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="destructive"
                                        className="bg-destructive hover:bg-destructive w-1/3"
                                        onClick={() =>
                                            setConfirmModalOpen(true)
                                        }
                                    >
                                        <Trash className="mr-2" />
                                        Delete Tier
                                    </Button>
                                </div>
                            </div>
                        </SheetFooter>
                    </form>
                </SheetContent>
            </Sheet>
            <NewPackSheet
                packtier={packtier}
                open={newPackSheetOpen}
                onOpenChange={setNewPackSheetOpen}
            />
            <ConfirmModal
                onConfirm={() =>
                    deleteMutation.mutate({ list, id: packtier.id })
                }
                open={confirmModalOpen}
                onOpenChange={setConfirmModalOpen}
                title="Delete Pack Tier"
                text={`Are you sure you want to delete the ${packtier.name}? This action cannot be undone.`}
                buttonText="Delete"
                buttonVariant="destructive"
            />
        </>
    );
};
